'use client';

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, Share2, Check, Loader2, Image as ImageIcon, Link as LinkIcon } from 'lucide-react';
import { toPng, toBlob } from 'html-to-image';
import { XIcon } from '@/components/icons/SocialIcons';
import { trackShare } from '@/lib/analytics';
import TokenShareCard from './TokenShareCard';
import type { MarketPulse, AbstractFearGreed } from '@/hooks/useMarketAnalysis';

interface TopMover {
  symbol: string;
  change: number;
}

interface TvlDataPoint {
  date: string;
  tvl: number;
}

interface TokenShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  pulse: MarketPulse;
  abstractFearGreed?: AbstractFearGreed | null;
  topGainers?: TopMover[];
  topLosers?: TopMover[];
  historicalTvl?: TvlDataPoint[];
  currentTvl?: number;
  tvlChange24h?: number;
}

export default function TokenShareModal({
  isOpen,
  onClose,
  pulse,
  abstractFearGreed,
  topGainers,
  topLosers,
  historicalTvl,
  currentTvl,
  tvlChange24h,
}: TokenShareModalProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Generate image on modal open
  useEffect(() => {
    if (isOpen && cardRef.current && !imageUrl) {
      generateImage();
    }
  }, [isOpen]);

  // Base options for image generation
  const getBaseImageOptions = useCallback(() => ({
    quality: 1,
    pixelRatio: 2,
    backgroundColor: '#0a0a0a',
    cacheBust: true,
    skipFonts: true,
  }), []);

  // Options that filter out external images (fallback for CORS issues)
  const getFilteredImageOptions = useCallback(() => ({
    ...getBaseImageOptions(),
    filter: (node: HTMLElement) => {
      if (node.tagName === 'IMG') {
        const src = (node as HTMLImageElement).src;
        if (src.startsWith('data:') || src.startsWith(window.location.origin) || src.startsWith('/')) {
          return true;
        }
        return false;
      }
      return true;
    },
  }), [getBaseImageOptions]);

  const generateImage = useCallback(async () => {
    if (!cardRef.current) return;

    setIsGenerating(true);
    setError(null);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));

      let dataUrl: string;
      try {
        dataUrl = await toPng(cardRef.current, getBaseImageOptions());
      } catch (firstError) {
        console.warn('First image generation attempt failed, retrying without external images:', firstError);
        dataUrl = await toPng(cardRef.current, getFilteredImageOptions());
      }

      setImageUrl(dataUrl);
    } catch (err) {
      console.error('Failed to generate image:', err);
      setError('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [getBaseImageOptions, getFilteredImageOptions]);

  const handleDownload = useCallback(async () => {
    if (!cardRef.current) return;

    setIsGenerating(true);
    try {
      let dataUrl: string;
      try {
        dataUrl = await toPng(cardRef.current, getBaseImageOptions());
      } catch (firstError) {
        console.warn('Download first attempt failed, retrying without external images:', firstError);
        dataUrl = await toPng(cardRef.current, getFilteredImageOptions());
      }

      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `abstract-token-market-${timestamp}.png`;
      link.href = dataUrl;
      link.click();

      trackShare({
        platform: 'other',
        contentType: 'coin',
        contentTitle: 'Abstract Token Market',
        contentUrl: window.location.href,
      });
    } catch (err) {
      console.error('Failed to download:', err);
      setError('Failed to download image.');
    } finally {
      setIsGenerating(false);
    }
  }, [getBaseImageOptions, getFilteredImageOptions]);

  const handleShareToX = useCallback(async () => {
    const fgValue = abstractFearGreed?.value ?? 50;
    const fgLabel = abstractFearGreed?.classification ?? 'Neutral';
    
    const topGainer = topGainers?.[0];
    const topLoser = topLosers?.[0];
    
    const lines: string[] = [
      `📊 Abstract Token Market Today:`,
      ``,
      `Fear & Greed: ${fgValue} (${fgLabel})`,
    ];
    
    if (topGainer) {
      lines.push(`🟢 Top Gainer: $${topGainer.symbol} +${topGainer.change.toFixed(1)}%`);
    }
    if (topLoser) {
      lines.push(`🔴 Top Loser: $${topLoser.symbol} ${topLoser.change.toFixed(1)}%`);
    }
    
    lines.push(``);
    lines.push(`${pulse.metrics.gainersCount} up / ${pulse.metrics.losersCount} down`);
    lines.push(``);
    lines.push(`Track Abstract on @descoutxyz 👀`);
    
    const tweetText = lines.join('\n');
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
    
    window.open(twitterUrl, '_blank', 'width=550,height=520');

    trackShare({
      platform: 'twitter',
      contentType: 'coin',
      contentTitle: 'Abstract Token Market',
      contentUrl: window.location.href,
    });
  }, [pulse, abstractFearGreed, topGainers, topLosers]);

  const handleCopyImage = useCallback(async () => {
    if (!cardRef.current) return;

    try {
      let blob: Blob | null;
      try {
        blob = await toBlob(cardRef.current, getBaseImageOptions());
      } catch (firstError) {
        console.warn('Copy first attempt failed, retrying without external images:', firstError);
        blob = await toBlob(cardRef.current, getFilteredImageOptions());
      }

      if (blob) {
        await navigator.clipboard.write([
          new ClipboardItem({ 'image/png': blob })
        ]);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);

        trackShare({
          platform: 'copy_link',
          contentType: 'coin',
          contentTitle: 'Abstract Token Market',
          contentUrl: window.location.href,
        });
      }
    } catch (err) {
      console.error('Failed to copy image:', err);
      try {
        await navigator.clipboard.writeText(pulse.summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        setError('Failed to copy. Please try downloading instead.');
      }
    }
  }, [pulse, getBaseImageOptions, getFilteredImageOptions]);

  if (!isOpen || !mounted) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden">
      <div 
        className="absolute inset-0 bg-black/95"
        onClick={onClose}
      />

      <div className="relative bg-neutral-900 rounded-2xl border border-neutral-800 shadow-2xl w-[calc(100%-16px)] max-w-[640px] max-h-[calc(100vh-32px)] overflow-y-auto mx-2">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-800">
          <div className="flex items-center gap-2 sm:gap-3">
            <Share2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400" />
            <h2 className="text-base sm:text-lg font-semibold text-white">Share Token Market</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 sm:p-2 hover:bg-neutral-800 rounded-lg transition-colors"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-neutral-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {/* preview: scale-to-fit on mobile (display only — export captures cardRef at natural 480px) */}
          <div className="flex justify-center mb-4 sm:mb-6">
            <div className="origin-top scale-[0.72] sm:scale-100 -mb-[28%] sm:mb-0">
              <div className="relative w-[480px]">
                {isGenerating && (
                  <div className="absolute inset-0 bg-neutral-900/80 rounded-2xl flex items-center justify-center z-10">
                    <Loader2 className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-400 animate-spin" />
                  </div>
                )}

                <TokenShareCard
                  ref={cardRef}
                  pulse={pulse}
                  abstractFearGreed={abstractFearGreed}
                  topGainers={topGainers}
                  topLosers={topLosers}
                  historicalTvl={historicalTvl}
                  currentTvl={currentTvl}
                  tvlChange24h={tvlChange24h}
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-950/50 border border-red-900/50 rounded-lg text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          <div className="bg-neutral-800/50 rounded-xl p-3 sm:p-4">
            <div className="flex items-start gap-2 sm:gap-3">
              <ImageIcon className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-neutral-300 leading-relaxed">
                <strong className="text-white">Tip:</strong> Download the image, then attach it to your X post for maximum engagement!
              </p>
            </div>
          </div>
        </div>

        {/* Actions Footer */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-neutral-800 bg-neutral-900/80">
          <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
            <button
              onClick={handleDownload}
              disabled={isGenerating}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white text-sm sm:text-base font-medium rounded-xl transition-all disabled:opacity-50 shadow-lg shadow-emerald-500/20"
            >
              {isGenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              <span className="hidden sm:inline">Download</span> Image
            </button>

            <button
              onClick={handleShareToX}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 bg-black hover:bg-neutral-900 text-white text-sm sm:text-base font-medium rounded-xl border border-neutral-700 transition-all"
            >
              <XIcon className="w-4 h-4" />
              Share on X
            </button>

            <button
              onClick={handleCopyImage}
              disabled={isGenerating}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl text-sm sm:text-base font-medium transition-all border ${
                copied
                  ? 'bg-green-600 border-green-500 text-white'
                  : 'bg-neutral-800 border-neutral-700 text-neutral-300 hover:bg-neutral-700 hover:text-white'
              }`}
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied!
                </>
              ) : (
                <>
                  <LinkIcon className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
